import { Suspense, useCallback, useMemo, useState } from 'react';
import { Outlet, useNavigation, useParams, Link } from 'react-router';
import Sidebar from './Sidebar';
import { ThemeProvider } from '@/lib/components/theme-provider';
import { Toaster } from '@/components/ui/toaster';
import Loader from '../components/Loader';
import Footer from './Footer';
import Meta from './Meta';
import { useCurrentUserData } from '@/lib/utils/hooks';
import { useQuery } from '@tanstack/react-query';
import { glacierPolicyApiGetLatestTransactionSummary } from '../heyapi';
import { Header } from './Header/index';
import { PermissionsContext } from '@/lib/layout/PermissionContext';
import { hasPermission } from './utils';
import type { PermissionGroup, userRole } from './types';
import { userRoleEnum } from './types';
import { CurrentTransactionContext } from './CurrentTransactionContext';
import { useEditMode } from './useEditMode';
import { RatingErrorsContext } from '@/lib/layout/RatingErrorsContext';
import type { FieldValidationError } from '@/lib/heyapi/types.gen';
import { useAgentAuthority } from './useAgentAuthority';

const Layout = () => {
  const { id } = useParams();
  const navigation = useNavigation();
  const currentUserData = useCurrentUserData();
  const [errors, setErrors] = useState<FieldValidationError[] | null>(null);
  const { editMode, setEditMode, isTravelTime } = useEditMode(id);
  const { agentAuthority, refetchAgentAuthority } = useAgentAuthority({
    policyId: id!,
  });

  const isLoading = navigation.state === 'loading';
  const role =
    (currentUserData.currentUser.data?.role as userRole) || userRoleEnum.AGENT;

  const { data: transactionData } = useQuery({
    queryKey: ['latestTransactionSummary', id],
    queryFn: () =>
      glacierPolicyApiGetLatestTransactionSummary({
        path: {
          account_id: id!,
        },
      }),
    enabled: id !== undefined,
  });

  // current transaction data
  const transactionInfo = useMemo(() => {
    const data = transactionData?.data;
    return {
      currentTransactionStatus: data?.currentTransactionStatus || null,
      currentTransactionType: data?.currentTransactionType || null,
      currentTransactionEffectiveDate:
        data?.currentTransactionEffectiveDate || null,
    };
  }, [transactionData?.data]);

  const hasExactRole = useCallback(
    (exactRole: userRole) => role === exactRole,
    [role]
  );

  const isSystemAdmin = useCallback(
    () => role === userRoleEnum.SYSTEM_ADMIN,
    [role]
  );

  const hasPermissionWrapper = useCallback(
    (group: PermissionGroup) => hasPermission(group, role),
    [role]
  );

  const permissionsContextValue = useMemo(
    () => ({
      isTravelTime,
      editMode,
      setEditMode,
      role,
      hasPermission: hasPermissionWrapper,
      hasExactRole,
      isSystemAdmin,
      agentAuthority,
      refetchAgentAuthority,
    }),
    [
      isTravelTime,
      editMode,
      setEditMode,
      role,
      hasPermissionWrapper,
      hasExactRole,
      isSystemAdmin,
      agentAuthority,
      refetchAgentAuthority,
    ]
  );

  const transactionContextValue = useMemo(
    () => transactionInfo,
    [transactionInfo]
  );

  const ratingErrorsContextValue = useMemo(
    () => ({ errors, setErrors }),
    [errors, setErrors]
  );

  return (
    <ThemeProvider>
      <RatingErrorsContext.Provider value={ratingErrorsContextValue}>
        <PermissionsContext.Provider value={permissionsContextValue}>
          <CurrentTransactionContext.Provider value={transactionContextValue}>
            <Meta />
            {isLoading ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50">
                <Loader />
              </div>
            ) : null}
            <div className="relative flex flex-col dark:bg-black dark:text-white">
              <Header />
              <div className="flex flex-col pl-[20vw]">
                <Sidebar />
                <main className="wrapper mx-auto w-full max-w-full pt-0">
                  {' '}
                  {/* w-[93%] */}
                  {/* TODO: See if shads class "wrapper" is better */}
                  {/* TODO: Add fallback component */}
                  <Suspense fallback={<Loader />}>
                    <Outlet />
                  </Suspense>
                  {errors && (
                    <Link
                      to={`/policy/${id}/policy-decisions?tab=ratingErrorMessages`}
                      className="fixed bottom-8 rounded bg-red-500 px-4 py-2 text-white"
                    >
                      Go To Errors Page
                    </Link>
                  )}
                </main>
                <Footer />
                <Toaster />
              </div>
            </div>
          </CurrentTransactionContext.Provider>
        </PermissionsContext.Provider>
      </RatingErrorsContext.Provider>
    </ThemeProvider>
  );
};

export default Layout;
